/** Big, high-contrast, glove-friendly controls (≥ 56 px) for outdoor iPad use. */
import { useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

type Tone = 'go' | 'stop' | 'neutral' | 'warn' | 'light';
const tones: Record<Tone, string> = {
  go: 'bg-green-700 text-white active:bg-green-800',
  stop: 'bg-red-700 text-white active:bg-red-800',
  warn: 'bg-amber-500 text-black active:bg-amber-600',
  neutral: 'bg-slate-800 text-white active:bg-slate-900',
  light: 'bg-white text-slate-900 active:bg-slate-200',
};

export function Big({ tone = 'neutral', className = '', ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }) {
  return (
    <button
      type="button"
      {...p}
      className={`min-h-[64px] rounded-xl px-5 text-lg font-bold shadow disabled:opacity-40 ${tones[tone]} ${className}`}
    />
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">{title}</h2>
          <button className="min-h-[56px] min-w-[56px] text-3xl" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const bigInput = 'w-full rounded-xl border-2 border-slate-400 p-4 text-xl focus:border-blue-700 focus:outline-none';

/**
 * Text box for notes. Mechanics talk with the iPad keyboard's 🎤 key (Apple dictation, free and
 * on-device on current iPads — OPEN_QUESTIONS D17). Nothing here may block it: no
 * autocomplete="off", spellcheck and autocorrect stay on, sentences auto-capitalise.
 */
export function NoteBox({ className = '', hint = true, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement> & { hint?: boolean }) {
  return (
    <>
      <textarea
        {...p}
        autoCapitalize="sentences"
        autoCorrect="on"
        spellCheck
        enterKeyHint="done"
        className={`${bigInput} min-h-[180px] leading-relaxed ${className}`}
      />
      {hint && <p className="mt-1 text-base text-slate-600">🎤 Tap the microphone on the keyboard to talk instead of typing.</p>}
    </>
  );
}

/**
 * Opens the iPad camera directly (capture=), never the photo library, so every photo keeps its
 * EXIF time and is taken on the spot (D7).
 */
export function CameraButton({ onPhoto, children, ...p }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & { onPhoto: (f: File) => void; tone?: Tone }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Big {...p} onClick={() => ref.current?.click()}>{children}</Big>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) onPhoto(f);
        e.target.value = '';
      }} />
    </>
  );
}

/** A row of big choice buttons (OK / ISSUE, gauge levels, Yes / No...). */
export function Choice<T extends string>({ options, value, onChange, labels, disabled, tones }: { options: readonly T[]; value: T | null | undefined; onChange: (v: T) => void; labels?: Partial<Record<T, string>>; disabled?: boolean; tones?: Partial<Record<T, Tone>> }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Big key={o} disabled={disabled} tone={value === o ? (tones?.[o] ?? 'go') : 'light'} className={`min-h-[56px] px-4 ${value === o ? '' : 'border-2 border-slate-300'}`} onClick={() => onChange(o)}>
          {labels?.[o] ?? o}
        </Big>
      ))}
    </div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <div className="mb-1 mt-4 text-lg font-bold">{children}</div>;
}

/** Plain field: autocorrect on so the dictation key still works. */
export function BigField({ label, ...p }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block">
      <span className="mb-1 mt-3 block text-lg font-bold">{label}</span>
      <input {...p} className={bigInput} />
    </label>
  );
}
